import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";

import { Form, Input, Spin, notification } from "antd";
import { PrimaryButton } from "components/SuperAdminAndBusiness/PrimaryButton";
import { AuthContext } from "contexts/AuthContext";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { changePassword } from "services/userService";

export const AdminPasswordInformationsProfileForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useContext(AuthContext);
  const { t }: { t: (key: string) => string } = useTranslation();

  const handleChangePassword = async () => {
    try {
      setLoading(true);

      if (user && user.uuid) {
        const response = await changePassword(user.uuid, {
          oldPassword: form.getFieldValue("oldPassword"),
          newPassword: form.getFieldValue("newPassword"),
        });

        if (response) {
          notification.success({
            placement: "bottomRight",
            message: t("passwordChangedSuccessfully"),
          });

          form.resetFields();
        }
      }
    } catch (error: any) {
      notification.error({
        placement: "bottomRight",
        message:
          error?.response?.data?.message ||
          t("anErrorHasOccurredWhileChangingPasswordPleaseTryAgain"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      autoComplete="off"
      className="w-full flex flex-col gap-5 items-end"
      onFinish={handleChangePassword}
    >
      <Spin tip={t("loading")} spinning={loading} fullscreen />

      <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        <Form.Item
          name="oldPassword"
          label={<span className="font-bold">{t("currentPassword")}:</span>}
          rules={[{ required: true, message: t("thisFieldIsRequired") }]}
        >
          <Input.Password
            id="oldPassword"
            placeholder="************"
            className="rounded-md"
            allowClear
            size="large"
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label={<span className="font-bold">{t("newPassword")}:</span>}
          rules={[
            {
              required: true,
              message: t("pleaseInsertYourNewPassword"),
            },
            {
              min: 8,
              max: 32,
              message: t("passwordMustBeBetween8And32Characters"),
            },
            {
              pattern:
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,32}$/,
              message: t(
                "passwordMustContainAtLeast1UppercaseLetter1LowercaseLetter1NumberAnd1SpecialCharacter"
              ),
            },
          ]}
        >
          <Input.Password
            id="newPassword"
            placeholder="************"
            className="rounded-md"
            allowClear
            size="large"
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item
          name="newPasswordConfirmation"
          label={
            <span className="font-bold">{t("newPasswordConfirmation")}:</span>
          }
          dependencies={["newPassword"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t("passwordsDoNotMatch")));
              },
            }),
            { required: true, message: t("thisFieldIsRequired") },
            { min: 6, message: t("passwordMustBeAtLeast6Characters") },
          ]}
        >
          <Input.Password
            id="newPasswordConfirmation"
            placeholder="************"
            className="rounded-md"
            allowClear
            size="large"
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
      </div>

      <PrimaryButton
        className="flex items-center justify-center"
        disabled={loading}
        loading={loading}
        htmlType="submit"
      >
        {t("changePassword")}
      </PrimaryButton>
    </Form>
  );
};
